// src/services/userService.js
import api from "./api";

// ─────────────────────────────────────────────────────────────────────────────
// AUTENTICAÇÃO
// ─────────────────────────────────────────────────────────────────────────────

export function login(credentials) {
  return api.post("/auth/login", credentials);
}

export function register(userData) {
  return api.post("/auth/register", userData);
}

/**
 * Envia e-mail + código de 6 dígitos para ativar a conta (Pending → Active).
 */
export function verifyEmail(verificationData) {
  return api.post("/auth/verify-email", verificationData);
}

/**
 * Antes de a pessoa preencher o cadastro inteiro: o e-mail está "free" (livre),
 * "claimable" (conta de membro de círculo sem assinatura própria - o cadastro
 * reaproveita a conta) ou "taken" (já tem dono). Só devolve o status.
 * POST /api/auth/register/precheck
 */
export function precheckRegistrationEmail(email) {
  return api.post("/auth/register/precheck", { email });
}

// ── PATCH: reenvio do código de verificação de e-mail ────────────────────────
/**
 * Solicita um novo código de verificação de e-mail (apenas contas Pending).
 * @param {{ email: string }} data
 */
export function resendVerificationCode(data) {
  return api.post("/auth/resend-verification-code", data);
}
// ── FIM PATCH ────────────────────────────────────────────────────────────────

export function loginWithGoogle(idToken) {
  return api.post("/auth/google", { idToken });
}

/**
 * Troca o código de uso único devolvido pelo fluxo redirect do Google pelo
 * resultado do login. O corpo é o mesmo de /auth/google.
 */
export function exchangeGoogleRedirectCode(code) {
  return api.post("/auth/google/exchange", { code });
}

/** Desativa a conta do usuário logado. */
export function deactivateMe() {
  return api.post("/auth/me/deactivate");
}

/** Reativa uma conta previamente desativada. */
export function reactivateAccount(payload = {}) {
  return api.post("/auth/reactivate", payload);
}

// ─────────────────────────────────────────────────────────────────────────────
// CRUD DE USUÁRIOS
// ─────────────────────────────────────────────────────────────────────────────

/** Lista todos os usuários (sem paginação). */
export function fetchUsers() {
  return api.get("/users");
}

/** Retorna um único usuário com plano, grupos e roles. */
export function getUserById(id) {
  return api.get(`/users/${id}`);
}

/**
 * Cria um usuário via multipart/form-data (POST /users).
 *
 * O backend usa [FromForm] + [Consumes("multipart/form-data")], portanto
 * o payload DEVE ser sempre um FormData — exatamente como o updateUser.
 *
 * Campos esperados no FormData:
 *   userName, email, password (opcional),
 *   statusString ("Active" | "Pending" | "Inactive" | "Suspended"),
 *   onboardingComplete, fullName, birthDate,
 *   bio, profession, country,
 *   photoFile (opcional, IFormFile),
 *   phoneE164, phoneIsWhatsapp, isWhatsappPublic,
 *   telegramUsername, instagramUsername, linkedInUsername, facebookUsername,
 *   groupIds[] (um append por ID),
 *   groupUserCount, activeNotesCount, activeTextsCount
 *
 * @param {FormData} data - Sempre FormData (construído por handleSubmit() no AddUserForm)
 */
export function createUser(data) {
  if (!(data instanceof FormData)) {
    // Salvaguarda: converte objetos planos em FormData para não quebrar o binding
    // do [FromForm]. Cobre chamadas legadas que ainda passem JSON.
    const fd = new FormData();
    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined) continue;
      if (Array.isArray(value)) {
        value.forEach((v) => fd.append(key, String(v)));
      } else {
        fd.append(key, typeof value === "boolean" ? String(value) : value);
      }
    }
    data = fd;
  }

  return api.post("/users", data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

/**
 * Atualiza um usuário existente.
 *
 * O backend usa [FromForm] + [Consumes("multipart/form-data")], portanto
 * o payload DEVE ser sempre um FormData — tanto com foto quanto sem.
 *
 * Campos chave no FormData:
 *   userId, userName, email, fullName, birthDate,
 *   statusString ("Active" | "Inactive" | "Pending" | "Suspended"),
 *   onboardingComplete, password (opcional),
 *   photoFile (opcional, IFormFile),
 *   photoPath (opcional, preserva foto atual quando photoFile ausente),
 *   groupIds[] (um append por ID),
 *   phoneE164, phoneIsWhatsapp, isWhatsappPublic,
 *   telegramUsername, instagramUsername, linkedInUsername, facebookUsername,
 *   bio, profession, country,
 *   groupUserCount, activeNotesCount, activeTextsCount
 *
 * @param {number}   id   - ID do usuário na rota
 * @param {FormData} data - Sempre FormData (construído por buildFormData() no EditUserForm)
 */
export function updateUser(id, data) {
  if (!(data instanceof FormData)) {
    // Salvaguarda: converte objetos planos em FormData para não quebrar o binding
    // do [FromForm]. Útil caso algum caminho de código antigo ainda passe JSON.
    const fd = new FormData();
    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined) continue;
      if (Array.isArray(value)) {
        value.forEach((v) => fd.append(key, String(v)));
      } else {
        fd.append(key, typeof value === "boolean" ? String(value) : value);
      }
    }
    data = fd;
  }

  return api.put(`/users/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export function deleteUser(id) {
  return api.delete(`/users/${id}`);
}

/** Exclui múltiplos usuários em lote (IDs no body). */
export function deleteUsersBatch(ids) {
  return api.delete("/users", { data: ids });
}

/** Lista usuários com paginação e busca. */
export function fetchUsersPaged(pageNumber, pageSize, search = "") {
  return api.get("/users/paged", {
    params: { pageNumber, pageSize, search },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// RELAÇÃO USUÁRIO-GRUPO
// ─────────────────────────────────────────────────────────────────────────────

/** Atribui um usuário a um grupo. */
export function assignUserToGroup(userId, groupId) {
  return api.post(`/users/${userId}/groups/${groupId}`);
}

/** Remove um usuário de um grupo pelo ID da associação (user_group_id). */
export function removeUserFromGroup(userGroupId) {
  return api.delete(`/user-groups/${userGroupId}`);
}

/** Retorna todos os grupos de um usuário. */
export function fetchGroupsForUser(userId) {
  return api.get(`/users/${userId}/groups`);
}

// ─────────────────────────────────────────────────────────────────────────────
// ONBOARDING
// ─────────────────────────────────────────────────────────────────────────────

/** Marca o onboarding do usuário logado como concluído. */
export function completeOnboarding() {
  return api.post("/users/me/complete-onboarding");
}

/** Marca o modal de boas-vindas do fundador como já exibido (aparece uma única vez). */
export function dismissFounderWelcome() {
  return api.post("/users/me/dismiss-founder-welcome");
}

/** Confirma ao backend que o frontend consumiu o evento de conversão de pagamento. */
export function acknowledgePaymentConversion() {
  return api.post("/users/me/acknowledge-payment-conversion");
}

// ─────────────────────────────────────────────────────────────────────────────
// RECUPERAÇÃO E ALTERAÇÃO DE SENHA
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Solicita código de redefinição de senha por e-mail.
 * @param {{ email: string }} data
 */
export function forgotPassword(data) {
  return api.post("/auth/forgot-password", data);
}

/**
 * Redefine a senha usando o código de verificação.
 * @param {{ email: string, code: string, newPassword: string }} data
 */
export function resetPassword(data) {
  return api.post("/auth/reset-password", data);
}

/**
 * Altera a senha do usuário logado.
 * Endpoint dedicado — NÃO usa updateUser.
 * @param {{ currentPassword: string, newPassword: string, confirmNewPassword: string }} param0
 */
export function changePassword({
  currentPassword,
  newPassword,
  confirmNewPassword,
}) {
  return api.post("/users/me/change-password", {
    currentPassword,
    newPassword,
    confirmNewPassword,
  });
}
